import { useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';

import { UnifiedLearningCertification } from 'constants/types';
import { getNormalizedCertmetricsCertificates } from './getNormalizedCertmetricsCertificates';
import { CERTIFICATE_NAME_ABBREVIATION } from 'constants/index';
import {
  UnifiedLearningCertificationQuery,
  UnifiedLearningCertificationQuery$data,
  UnifiedLearningCertificationQuery$variables,
} from 'graphql/__generated__/UnifiedLearningCertificationQuery.graphql';
import { UNIFIED_LEARNING_CERTIFICATES } from 'graphql/UnifiedLearningCertificationQuery';

type CertificatesByActiveDatePresence = {
  certificatesWithDate: UnifiedLearningCertification[];
  certificatesWithoutDate: UnifiedLearningCertification[];
};

const splitCertificatesByActiveDatePresence = (
  certificates: UnifiedLearningCertification[],
) => {
  return certificates.reduce<CertificatesByActiveDatePresence>(
    (result, currentCertificate) => {
      if (currentCertificate?.activeDate) {
        result.certificatesWithDate.push(currentCertificate);
      } else {
        result.certificatesWithoutDate.push(currentCertificate);
      }

      return result;
    },
    { certificatesWithDate: [], certificatesWithoutDate: [] },
  );
};

const sortCertificatesByActiveDate = (
  certificates: UnifiedLearningCertification[],
) => {
  const { certificatesWithDate, certificatesWithoutDate } =
    splitCertificatesByActiveDatePresence(certificates);

  const sortedCertificates = certificatesWithDate.sort((a, b) => {
    const aActiveDate = a?.activeDate || '';
    const bActiveDate = b?.activeDate || '';

    return new Date(bActiveDate).getTime() - new Date(aActiveDate).getTime();
  });

  return sortedCertificates.concat(certificatesWithoutDate);
};

const sortEmeritusCertificateFirst = (
  certificates: UnifiedLearningCertification[],
) => {
  const emeritusCertificateIndex = certificates.findIndex(
    (certificate) =>
      certificate?.nameAbbr === CERTIFICATE_NAME_ABBREVIATION.EXPERT_EMERITUS,
  );

  if (emeritusCertificateIndex && emeritusCertificateIndex !== -1) {
    const emeritusCertificte = certificates.splice(emeritusCertificateIndex, 1);
    certificates.unshift(emeritusCertificte[0]);
  }

  return certificates;
};

const retrieveCertmetricsCertificates = (
  data: UnifiedLearningCertificationQuery$data,
): UnifiedLearningCertification[] | undefined => {
  const normalizedCertificates = getNormalizedCertmetricsCertificates(data);

  if (!normalizedCertificates) {
    return undefined;
  }

  const dateSorted = sortCertificatesByActiveDate(normalizedCertificates);
  return sortEmeritusCertificateFirst(dateSorted);
};

type UseCertmetricsCertificatesResult = {
  certificates: UnifiedLearningCertification[] | undefined;
  totalCount: number | undefined;
};

const useCertmetricsCertificates = (
  variables: UnifiedLearningCertificationQuery$variables,
): UseCertmetricsCertificatesResult => {
  const data = useLazyLoadQuery<UnifiedLearningCertificationQuery>(
    UNIFIED_LEARNING_CERTIFICATES,
    variables,
    { fetchPolicy: 'store-or-network' },
  );

  const certificates = useMemo(() => {
    return retrieveCertmetricsCertificates(data);
  }, [data]);

  const totalCount =
    data.unified?.unifiedProfile?.learning?.certifications?.totalCount ??
    undefined;

  return {
    certificates,
    totalCount,
  };
};
export default useCertmetricsCertificates;
